import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ReportHeader } from '../../components/ReportHeader';
import { SortableTable } from '../../components/SortableTable';
import { ExportButton } from '../../components/ExportButton';
import { ChevronDown, ChevronRight, Award, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(n) { return (n ?? 0).toLocaleString(); }

// ── sub-components ────────────────────────────────────────────────────────────
function KpiCard({ label, value, accent, testId }) {
  return (
    <div className="stat-card" data-testid={testId} style={{ borderTop: `3px solid ${accent}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 15, fontWeight: 700, color: '#1e293b',
      margin: '24px 0 10px', letterSpacing: '-0.01em',
    }}>
      {children}
    </h2>
  );
}

function PartnerGroupPill({ group }) {
  const styles = {
    'ESI_MPL_GSI': { bg: '#dbeafe', color: '#1d4ed8' },
    'CSP':         { bg: '#dcfce7', color: '#15803d' },
  };
  const s = styles[group] || { bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px',
      borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>
      {group || '—'}
    </span>
  );
}

function CompletionRateCell({ value }) {
  const v = Number(value || 0);
  const color = v >= 70 ? '#16a34a' : v >= 40 ? '#d97706' : '#64748b';
  return <span style={{ color, fontWeight: 600 }}>{v.toFixed(1)}%</span>;
}

function ProgressBar({ value }) {
  const v = Math.min(100, Math.max(0, Number(value || 0)));
  const color = v >= 70 ? '#22c55e' : v >= 40 ? '#f59e0b' : '#94a3b8';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden',
      }}>
        <div style={{ width: `${v}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', minWidth: 34 }}>
        {v.toFixed(0)}%
      </span>
    </div>
  );
}

// ── Activity Dashes ───────────────────────────────────────────────────────────
function ActivityDashes({ row }) {
  const total = Math.max(0, Number(row.total_courses || 0));
  if (total === 0) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;

  const completionRate = Number(row.completion_rate || 0) / 100;
  const avgProgress    = Number(row.avg_progress    || 0) / 100;

  // green = proportion that fully completed; amber = in-progress learners (not yet done)
  const greenCount = Math.round(total * completionRate);
  const amberCount = Math.max(0, Math.round(total * avgProgress) - greenCount);
  const grayCount  = Math.max(0, total - greenCount - amberCount);

  const dashes = [
    ...Array(greenCount).fill('completed'),
    ...Array(amberCount).fill('in_progress'),
    ...Array(grayCount).fill('not_started'),
  ];

  const colorMap = {
    completed:   '#22c55e',
    in_progress: '#f59e0b',
    not_started: '#e2e8f0',
  };

  const parts = [
    greenCount > 0 ? `${greenCount} completed`   : '',
    amberCount > 0 ? `${amberCount} in progress` : '',
    grayCount  > 0 ? `${grayCount} not started`  : '',
  ].filter(Boolean);

  const tooltip = `${total} courses: ${parts.join(', ')}`;
  const dashW   = Math.max(5, Math.min(14, Math.floor(140 / total)));

  return (
    <div
      title={tooltip}
      data-testid={`activity-dashes-${row.journey_id}`}
      style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'default' }}
    >
      {dashes.map((status, i) => (
        <div
          key={i}
          style={{
            width: dashW, height: 20, borderRadius: 3,
            background: colorMap[status], flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

// ── Inline detail panel ───────────────────────────────────────────────────────
function DetailPanel({ data, loading }) {
  if (loading) {
    return (
      <div style={{ padding: '16px 24px', color: '#64748b', fontSize: 13 }}>
        Loading journey details…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      data-testid="journey-detail-panel"
      style={{
        padding: '16px 24px 20px',
        background: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
      }}
    >
      {data.description && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
          {data.description}
        </p>
      )}
      {data.partner_segments && (
        <div style={{ marginBottom: 14, fontSize: 12 }}>
          <span style={{ color: '#94a3b8' }}>Restricted to: </span>
          <span style={{ fontWeight: 600, color: '#475569' }}>{data.partner_segments}</span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Phases */}
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Phases ({data.phases.length})
          </div>
          {data.phases.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>No phases defined</div>
          ) : (
            data.phases.map((p, i) => (
              <div key={i} style={{ fontSize: 12, color: '#475569', marginBottom: 5 }}>
                <span style={{
                  fontWeight: 700,
                  color: '#1e293b',
                  background: '#e2e8f0',
                  borderRadius: 4,
                  padding: '1px 6px',
                  marginRight: 6,
                  fontSize: 11,
                }}>
                  {p.phase_order}
                </span>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                {p.description && (
                  <span style={{ color: '#94a3b8' }}> — {p.description}</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Top 5 Partners */}
        <div>
          <div style={{
            fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Top Partners
          </div>
          {data.top_partners.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>No partner data available</div>
          ) : (
            data.top_partners.slice(0, 5).map((p, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 12, color: '#475569', marginBottom: 5,
              }}>
                <span>
                  <span style={{ color: '#94a3b8', marginRight: 4 }}>{i + 1}.</span>
                  {p.partner_name || '—'}
                </span>
                <span style={{ fontWeight: 700, color: '#1e293b' }}>
                  {fmt(p.enrolled_learners)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Column definitions for partner + badges tabs ──────────────────────────────
const PARTNER_COLS = [
  { key: 'partner_name',      label: 'Partner Name',  sortable: true, maxWidth: 260 },
  {
    key: 'partner_group', label: 'Group', sortable: true,
    render: v => <PartnerGroupPill group={v} />,
  },
  { key: 'enrolled_learners', label: 'Enrolled',      align: 'right', sortable: true },
  { key: 'completions',       label: 'Completions',   align: 'right', sortable: true },
  {
    key: 'completion_rate', label: 'Completion %', align: 'right', sortable: true,
    render: v => <CompletionRateCell value={v} />,
  },
  { key: 'badges_awarded',    label: 'Badges',        align: 'right', sortable: true },
];

const BADGE_COLS = [
  { key: 'journey_name',   label: 'Journey Name',   sortable: true, maxWidth: 300 },
  { key: 'project',        label: 'Project',        sortable: true, maxWidth: 160 },
  {
    key: 'badges_awarded', label: 'Badges Awarded', align: 'right', sortable: true,
    render: v => (
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
        <Award size={13} color="#f59e0b" />
        <span style={{ fontWeight: 700 }}>{fmt(v)}</span>
      </span>
    ),
  },
  { key: 'enrolled_learners', label: 'Enrolled',    align: 'right', sortable: true },
  {
    key: 'badge_rate', label: 'Badge Rate %', align: 'right', sortable: true,
    render: v => <CompletionRateCell value={v} />,
  },
];

const EXPORT_COLUMNS = [
  { key: 'journey_name',      label: 'Journey Name' },
  { key: 'target_role',       label: 'Target Role' },
  { key: 'solution_area',     label: 'Solution Area' },
  { key: 'project',           label: 'Project' },
  { key: 'total_courses',     label: 'Total Courses' },
  { key: 'enrolled_learners', label: 'Enrolled Learners' },
  { key: 'completions',       label: 'Completions' },
  { key: 'completion_rate',   label: 'Completion Rate %' },
  { key: 'avg_progress',      label: 'Avg Progress %' },
  { key: 'badges_awarded',    label: 'Badges Awarded' },
];

const TABS = [
  { id: 'journeys',   label: 'Journeys' },
  { id: 'by-partner', label: 'By Partner' },
  { id: 'badges',     label: 'Badges' },
];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function JourneyReport() {
  const navigate = useNavigate();
  const [selectedFy, setSelectedFy]       = useState('');
  const [activeTab, setActiveTab]         = useState('journeys');

  const [summary, setSummary]             = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [journeyRows, setJourneyRows]     = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const [partnerRows, setPartnerRows]     = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const [badgeRows, setBadgeRows]         = useState([]);
  const [badgeLoading, setBadgeLoading]   = useState(false);

  const [journeySearch, setJourneySearch] = useState('');
  const [solutionAreaFilter, setSolutionAreaFilter] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');

  const [expandedJourneyId, setExpandedJourneyId] = useState(null);
  const [journeyDetail, setJourneyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [error, setError] = useState('');

  const searchDebounceRef = useRef(null);

  // ── Fetch all data when FY changes ─────────────────────────────────────────
  const fetchAll = useCallback(async (fy) => {
    if (!fy) return;
    setError('');
    setSummaryLoading(true);
    setJourneyLoading(true);
    setPartnerLoading(true);
    setBadgeLoading(true);
    try {
      const [sumRes, journeyRes, partnerRes, badgeRes] = await Promise.all([
        axios.get(`${API}/api/journeys/summary`,    { params: { fy_label: fy }, withCredentials: true }),
        axios.get(`${API}/api/journeys/list`,       { params: { fy_label: fy }, withCredentials: true }),
        axios.get(`${API}/api/journeys/by-partner`, { withCredentials: true }),
        axios.get(`${API}/api/journeys/badges`,     { withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      setJourneyRows(journeyRes.data);
      setPartnerRows(partnerRes.data);
      setBadgeRows(badgeRes.data);
    } catch (e) {
      setError('Failed to load journey data. Please try again.');
      console.error(e);
    } finally {
      setSummaryLoading(false);
      setJourneyLoading(false);
      setPartnerLoading(false);
      setBadgeLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(selectedFy); }, [selectedFy, fetchAll]);

  // ── Inline detail expand ───────────────────────────────────────────────────
  const handleJourneyClick = useCallback((journeyId) => {
    if (expandedJourneyId === journeyId) {
      setExpandedJourneyId(null);
      setJourneyDetail(null);
      return;
    }
    setExpandedJourneyId(journeyId);
    setJourneyDetail(null);
    setDetailLoading(true);
    axios.get(`${API}/api/journeys/${journeyId}/detail`, { withCredentials: true })
      .then(({ data }) => setJourneyDetail(data))
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [expandedJourneyId]);

  // ── Search debounce for journey search ─────────────────────────────────────
  const handleJourneySearchChange = (val) => {
    setJourneySearch(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {}, 300);
  };

  // ── Filtered rows (client-side) ────────────────────────────────────────────
  const solutionAreas = useMemo(() => {
    const seen = new Set();
    const result = [];
    for (const r of journeyRows) {
      if (r.solution_area && !seen.has(r.solution_area)) {
        seen.add(r.solution_area);
        result.push(r.solution_area);
      }
    }
    return result.sort();
  }, [journeyRows]);

  const filteredJourneyRows = useMemo(() => {
    let rows = journeyRows;
    if (solutionAreaFilter) rows = rows.filter(r => r.solution_area === solutionAreaFilter);
    if (journeySearch.trim()) {
      const q = journeySearch.toLowerCase();
      rows = rows.filter(r => r.journey_name?.toLowerCase().includes(q));
    }
    return rows;
  }, [journeyRows, journeySearch, solutionAreaFilter]);

  const filteredPartnerRows = useMemo(() => {
    if (!partnerSearch.trim()) return partnerRows;
    const q = partnerSearch.toLowerCase();
    return partnerRows.filter(r => r.partner_name?.toLowerCase().includes(q));
  }, [partnerRows, partnerSearch]);

  const totalBadges = useMemo(
    () => badgeRows.reduce((s, r) => s + (r.badges_awarded || 0), 0),
    [badgeRows],
  );

  // ── Export fn ──────────────────────────────────────────────────────────────
  const fetchExportData = useCallback(async () => {
    const res = await axios.get(`${API}/api/journeys/export`, {
      params: { fy_label: selectedFy },
      withCredentials: true,
    });
    return res.data;
  }, [selectedFy]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ReportHeader
        reportTitle="Learning Journeys"
        primaryTable="learning_journey_enrollments"
        onChange={({ fyLabel }) => setSelectedFy(fyLabel)}
      />

      <div data-testid="journey-report" style={{ padding: '0 16px' }}>
        {/* All-time data note */}
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '10px 0 4px', fontStyle: 'italic' }}>
          Journey data shown all-time — enrollment dates not tracked at journey level.
        </p>

        {/* Error */}
        {error && (
          <div data-testid="journey-error" style={{
            background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
            borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {summaryLoading && (
          <div data-testid="journey-loading" style={{
            color: '#64748b', fontSize: 14, padding: '32px 0', textAlign: 'center',
          }}>
            Loading…
          </div>
        )}

        {/* KPI cards */}
        {!summaryLoading && summary && (
          <>
            <SectionTitle>Key Metrics — {summary.fy_label}</SectionTitle>
            <div
              className="stats-grid"
              data-testid="journey-kpi-cards"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
            >
              <KpiCard label="Active Journeys"   value={fmt(summary.total_journeys)}     accent="#6366f1" testId="kpi-total-journeys" />
              <KpiCard label="Enrolled Learners" value={fmt(summary.enrolled_learners)}  accent="#3b82f6" testId="kpi-enrolled-learners" />
              <KpiCard label="Total Enrollments" value={fmt(summary.total_enrollments)}  accent="#06b6d4" testId="kpi-total-enrollments" />
              <KpiCard label="Completed"         value={fmt(summary.completed_journeys)} accent="#22c55e" testId="kpi-completed" />
              <KpiCard
                label="Completion Rate"
                value={`${Number(summary.completion_rate || 0).toFixed(1)}%`}
                accent="#8b5cf6"
                testId="kpi-completion-rate"
              />
              <KpiCard label="Badges Awarded"    value={fmt(summary.badges_awarded)}     accent="#f59e0b" testId="kpi-badges-awarded" />
            </div>
          </>
        )}

        {/* Tab bar */}
        <div
          data-testid="journey-tabs"
          style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginTop: 28 }}
        >
          {TABS.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 22px', fontSize: 13, fontWeight: 600,
                border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #6366f1' : '2px solid transparent',
                color: activeTab === tab.id ? '#6366f1' : '#64748b',
                marginBottom: -2, transition: 'color 0.15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab 1: Journeys ──────────────────────────────────────────────── */}
        {activeTab === 'journeys' && (
          <div data-testid="tab-content-journeys" style={{ marginTop: 16 }}>
            {/* Search + SA filter + Export row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <input
                  data-testid="journey-search"
                  type="text"
                  placeholder="Search journey name…"
                  value={journeySearch}
                  onChange={e => handleJourneySearchChange(e.target.value)}
                  style={{
                    height: 34, padding: '0 12px', fontSize: 13,
                    border: '1px solid #e2e8f0', borderRadius: 7,
                    outline: 'none', width: 240,
                  }}
                />
                <select
                  data-testid="journey-sa-filter"
                  value={solutionAreaFilter}
                  onChange={e => setSolutionAreaFilter(e.target.value)}
                  style={{
                    height: 34, padding: '0 10px', fontSize: 13,
                    border: '1px solid #e2e8f0', borderRadius: 7,
                    background: solutionAreaFilter ? '#f0f9ff' : '#fff',
                    color: solutionAreaFilter ? '#0369a1' : '#374151',
                    minWidth: 160, maxWidth: 260, cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <option value="">All Solution Areas</option>
                  {solutionAreas.map(sa => (
                    <option key={sa} value={sa}>{sa}</option>
                  ))}
                </select>
                {solutionAreaFilter && (
                  <button
                    data-testid="journey-sa-filter-reset"
                    onClick={() => setSolutionAreaFilter('')}
                    style={{
                      height: 34, padding: '0 10px', fontSize: 12, fontWeight: 600,
                      border: '1px solid #fca5a5', borderRadius: 7,
                      background: '#fff', color: '#dc2626', cursor: 'pointer',
                    }}
                  >
                    Clear
                  </button>
                )}
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  {filteredJourneyRows.length} / {journeyRows.length} journeys
                </span>
              </div>
              <ExportButton
                fetchAllFn={fetchExportData}
                filename={`learning_journeys_${selectedFy}`}
                columns={EXPORT_COLUMNS}
                label="Export CSV"
              />
            </div>

            {/* Journey table with inline expand */}
            <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="report-table" data-testid="journey-table">
                  <thead>
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Journey Name</th>
                      <th>Role</th>
                      <th>Solution Area</th>
                      <th>Project</th>
                      <th style={{ textAlign: 'right' }}>Phases</th>
                      <th style={{ textAlign: 'right' }}>Courses</th>
                      <th style={{ textAlign: 'right' }}>Enrolled</th>
                      <th style={{ textAlign: 'right' }}>Completions</th>
                      <th style={{ textAlign: 'right' }}>Completion %</th>
                      <th style={{ minWidth: 110 }}>Avg Progress</th>
                      <th
                        style={{ minWidth: 90, cursor: 'help' }}
                        title="Each bar = 1 required course. Green = completed, amber = in progress, grey = not started. Derived from avg. progress and completion rate."
                      >
                        Activity
                      </th>
                      <th style={{ textAlign: 'right' }}>Badges</th>
                      <th style={{ width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {journeyLoading ? (
                      <tr>
                        <td colSpan={13} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>
                          Loading journeys…
                        </td>
                      </tr>
                    ) : filteredJourneyRows.length === 0 ? (
                      <tr>
                        <td colSpan={13} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>
                          {journeySearch ? 'No results match your search.' : 'No active journeys found.'}
                        </td>
                      </tr>
                    ) : (
                      filteredJourneyRows.map(row => (
                        <React.Fragment key={row.journey_id}>
                          <tr
                            data-testid={`journey-row-${row.journey_id}`}
                            onClick={() => handleJourneyClick(row.journey_id)}
                            style={{
                              cursor: 'pointer',
                              background: expandedJourneyId === row.journey_id ? '#f8fafc' : '',
                            }}
                          >
                            <td style={{ textAlign: 'center', color: '#94a3b8' }}>
                              {expandedJourneyId === row.journey_id
                                ? <ChevronDown size={14} />
                                : <ChevronRight size={14} />
                              }
                            </td>
                            <td style={{ fontWeight: 600, color: '#1e293b', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.journey_name || '—'}
                            </td>
                            <td style={{ color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.target_role || '—'}
                            </td>
                            <td style={{ color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.solution_area || '—'}
                            </td>
                            <td style={{ color: '#64748b', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.project || '—'}
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt(row.total_phases)}</td>
                            <td style={{ textAlign: 'right' }}>{fmt(row.total_courses)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>
                              <button
                                data-testid={`enrolled-count-link-${row.journey_id}`}
                                onClick={e => { e.stopPropagation(); navigate(`/reports/journeys/${row.journey_id}`); }}
                                title="View learner detail"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#6366f1', fontWeight: 700, fontSize: 'inherit',
                                  padding: 0, textDecoration: 'underline',
                                  textDecorationStyle: 'dotted', textUnderlineOffset: 3,
                                }}
                              >
                                {fmt(row.enrolled_learners)}
                              </button>
                            </td>
                            <td style={{ textAlign: 'right' }}>{fmt(row.completions)}</td>
                            <td style={{ textAlign: 'right' }}>
                              <CompletionRateCell value={row.completion_rate} />
                            </td>
                            <td style={{ minWidth: 110 }}>
                              {row.avg_progress !== null
                                ? <ProgressBar value={row.avg_progress} />
                                : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                              }
                            </td>
                            <td style={{ minWidth: 90 }}>
                              <ActivityDashes row={row} />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              {row.badges_awarded > 0 ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                  <Award size={12} color="#f59e0b" />
                                  <span style={{ fontWeight: 600 }}>{fmt(row.badges_awarded)}</span>
                                </span>
                              ) : (
                                <span style={{ color: '#94a3b8' }}>0</span>
                              )}
                            </td>
                            {/* View Learner Detail button */}
                            <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                              <button
                                data-testid={`view-detail-${row.journey_id}`}
                                title="View learner detail"
                                onClick={e => { e.stopPropagation(); navigate(`/reports/journeys/${row.journey_id}`); }}
                                style={{
                                  background: '#f1f5f9', border: 'none', borderRadius: 6,
                                  padding: '4px 6px', cursor: 'pointer', color: '#6366f1',
                                  display: 'inline-flex', alignItems: 'center',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                                onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                              >
                                <Users size={13} />
                              </button>
                            </td>
                          </tr>
                          {expandedJourneyId === row.journey_id && (
                            <tr data-testid={`journey-detail-row-${row.journey_id}`}>
                              <td colSpan={14} style={{ padding: 0, borderBottom: '2px solid #e2e8f0' }}>
                                <DetailPanel data={journeyDetail} loading={detailLoading} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 2: By Partner ─────────────────────────────────────────────── */}
        {activeTab === 'by-partner' && (
          <div data-testid="tab-content-by-partner" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', marginBottom: 12 }}>
              <input
                data-testid="partner-search"
                type="text"
                placeholder="Search partner name…"
                value={partnerSearch}
                onChange={e => setPartnerSearch(e.target.value)}
                style={{
                  height: 34, padding: '0 12px', fontSize: 13,
                  border: '1px solid #e2e8f0', borderRadius: 7,
                  outline: 'none', width: 260,
                }}
              />
            </div>
            <div className="report-card">
              <SortableTable
                columns={PARTNER_COLS}
                data={filteredPartnerRows}
                loading={partnerLoading}
                defaultSort={{ key: 'enrolled_learners', direction: 'desc' }}
              />
            </div>
          </div>
        )}

        {/* ── Tab 3: Badges ─────────────────────────────────────────────────── */}
        {activeTab === 'badges' && (
          <div data-testid="tab-content-badges" style={{ marginTop: 16 }}>
            {/* Total badges display */}
            {!badgeLoading && badgeRows.length > 0 && (
              <div
                data-testid="total-badges-display"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 10,
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  border: '1px solid #fcd34d',
                  borderRadius: 10, padding: '10px 20px',
                  marginBottom: 16,
                }}
              >
                <Award size={24} color="#d97706" />
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#92400e', lineHeight: 1 }}>
                    {fmt(totalBadges)}
                  </div>
                  <div style={{ fontSize: 11, color: '#b45309', fontWeight: 500, marginTop: 2 }}>
                    Total Badges Awarded
                  </div>
                </div>
              </div>
            )}
            <div className="report-card">
              <SortableTable
                columns={BADGE_COLS}
                data={badgeRows}
                loading={badgeLoading}
                defaultSort={{ key: 'badges_awarded', direction: 'desc' }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
